import { useMessage } from "@/hooks/web/useMessage";

export interface VersionCheckOptions {
  // 检测间隔时间（毫秒），默认30秒
  checkInterval?: number;
  // 是否在开发模式下启用检测
  enableInDev?: boolean;
  // 自定义版本文件路径
  versionFile?: string;
}

class VersionChecker {
  private checkInterval: number;
  private enableInDev: boolean;
  private versionFile: string;
  private timer: NodeJS.Timeout | null = null;
  private isChecking = false;
  private showMessage = false;

  constructor(options: VersionCheckOptions = {}) {
    this.checkInterval = options.checkInterval || 30 * 1000;
    this.enableInDev = options.enableInDev || false;
    this.versionFile = options.versionFile || "/version.json";
  }

  // 获取版本哈希
  private async getVersionHash(): Promise<string | null> {
    try {
      const res = await fetch(`${this.versionFile}?t=${Date.now()}`, {
        headers: { "Cache-Control": "no-cache" },
      });

      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }

      const { hash } = await res.json();
      return hash || null;
    } catch (error) {
      console.error("获取版本哈希失败:", error);
      return null;
    }
  }

  // 获取当前存储的版本
  private getCurrentVersion(): string {
    return localStorage.getItem("appVersion") || "";
  }

  // 存储版本号
  private setCurrentVersion(version: string): void {
    localStorage.setItem("appVersion", version);
  }

  // 显示更新提示
  private async showUpdateNotification(): Promise<void> {
    if (this.showMessage) return;

    const message = useMessage();

    try {
      this.showMessage = true;
      await message.confirm("发现新版本，请刷新页面！");
      window.location.reload();
    } finally {
      this.showMessage = false;
    }
  }

  // 检查更新
  async checkUpdate(): Promise<boolean> {
    if (this.isChecking) return false;
    if (!this.shouldCheck()) return false;

    this.isChecking = true;

    try {
      const currentVersion = this.getCurrentVersion();
      const newHash = await this.getVersionHash();

      if (!newHash) return false;

      // 首次运行，存储版本号
      if (!currentVersion) {
        this.setCurrentVersion(newHash);
        return false;
      }

      // 检测到版本变化
      if (newHash !== currentVersion) {
        await this.showUpdateNotification();
        this.setCurrentVersion(newHash);
        return true;
      }

      console.log("当前版本已是最新");
      return false;
    } catch (error) {
      console.error("版本检测失败:", error);
      return false;
    } finally {
      this.isChecking = false;
    }
  }

  // 初始化版本号（用于应用启动时）
  async initializeVersion(): Promise<void> {
    if (!this.shouldCheck()) return;

    try {
      const hash = await this.getVersionHash();
      if (hash) {
        this.setCurrentVersion(hash);
      }
    } catch (error) {
      console.error("初始化版本号失败:", error);
    }
  }

  // 开始定时检测
  startAutoCheck(): void {
    if (!this.shouldCheck() || this.timer) return;

    // 立即初始化版本号
    setTimeout(() => {
      this.initializeVersion();
    }, 1000);

    // 开始定时检测
    this.timer = setInterval(() => {
      this.checkUpdate();
    }, this.checkInterval);
  }

  // 停止检测
  stopAutoCheck(): void {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  // 判断是否应该进行检测
  private shouldCheck(): boolean {
    if (import.meta.env.DEV && !this.enableInDev) {
      return false;
    }
    return ["prod", "test"].includes(import.meta.env.MODE);
  }

  // 手动触发检测
  async manualCheck(): Promise<boolean> {
    return await this.checkUpdate();
  }
}

// 创建默认实例
export const defaultVersionChecker = new VersionChecker();

// 组合式函数
export const useVersionCheck = (options?: VersionCheckOptions) => {
  const versionChecker = new VersionChecker(options);

  const start = () => versionChecker.startAutoCheck();
  const stop = () => versionChecker.stopAutoCheck();
  const check = () => versionChecker.manualCheck();
  const initialize = () => versionChecker.initializeVersion();

  return {
    start,
    stop,
    check,
    initialize,
  };
};
